import { KeyValueStore } from './storage.ts';

/**
 * 유입 경로(광고·제휴 출처) 기억하기.
 *
 *   https://www.urnplay.com/f/dongguk/event/bumjin1
 *     -> 'dongguk' 을 기억하고 /event/bumjin1 로 보낸다.
 *   https://www.urnplay.com/f/dongguk
 *     -> 'dongguk' 을 기억하고 / 로 보낸다.
 *
 * 앞머리가 `/f/` 인 이유는 광고 링크에 그대로 찍히는 값이라 짧을수록 좋아서다.
 * 앱의 실제 화면 주소 중에 `f` 로 시작하는 것은 없다.
 *
 * 기억한 코드는 **가입할 때 한 번** 서버로 보내 회원 정보에 남긴다
 * (`MC_USER.F_INFLOW`). 로그인마다 보내긴 하지만, 기존 회원의 값은 서버가
 * 덮어쓰지 않는다 — '처음 데려온 곳' 이라는 뜻을 지키기 위해서다.
 */

/**
 * ★ **localStorage 를 쓴다.** 앱의 다른 세션 값들은 sessionStorage 라 탭을 닫으면
 *   사라지는데, 유입 경로는 "오늘 광고로 들어와 구경만 하고 내일 가입" 하는 경우에도
 *   남아 있어야 한다. 그게 이 값의 존재 이유다.
 *
 * ★ 접두사는 다른 값들과 같은 `app:` 을 쓰되 저장소가 다르므로 섞이지 않는다.
 */
const store = new KeyValueStore<'inflow'>({ prefix: 'app:', driver: 'local' });

/** 유입 경로를 기억해 두는 기간. 이 안에 가입하면 그 출처로 잡힌다. */
const INFLOW_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * 받아들일 코드 모양.
 *
 * 주소에 그대로 쓰이고 DB 컬럼이 50바이트(`VARCHAR2(50)`)라 영문·숫자·`-`·`_` 만
 * 받는다. 이상한 값이 들어오면 **저장하지 않고 이동만** 시킨다 — 광고 링크가
 * 오타났다고 해서 사용자가 페이지를 못 보면 안 된다.
 */
const CODE_PATTERN = /^[A-Za-z0-9_-]{1,50}$/;

/** 기억해 둔 유입 코드. 없거나 만료됐으면 null */
export function getInflow(): string | null {
  return store.get<string>('inflow') ?? null;
}

/** 유입 코드를 기억한다. 모양이 맞지 않으면 무시한다. */
export function saveInflow(code: string): boolean {
  if (!CODE_PATTERN.test(code)) return false;

  /*
    ★ **마지막으로 누른 링크가 이긴다.** (2026-09-17 결정 — 마지막 링크 우선)

      이미 기억해 둔 코드가 있어도 덮어쓰고, 90일도 이 시점부터 다시 센다.
      가입 직전에 누른 링크가 가입을 직접 이끈 곳이라는 판단이다.

      처음에는 '먼저 들어온 출처를 지킨다' 로 만들었는데, `/f/dongguk` 으로 들어와
      가입했는데도 예전에 눌렀던 다른 코드가 기록돼 운영하는 쪽이 헷갈렸다.

    ★ 이 규칙은 **브라우저에 기억해 두는 코드**에만 해당한다. 서버는 여전히
      가입할 때 한 번만 적고, 이미 가입한 회원의 값은 바꾸지 않는다.
  */
  store.set<string>('inflow', code, INFLOW_TTL_MS);
  return true;
}

/** 가입이 끝나 더 들고 있을 이유가 없을 때 */
export function clearInflow(): void {
  store.remove('inflow');
}

/**
 * 게이트 주소면 코드를 기억하고 **실제로 가야 할 주소**를 돌려준다.
 * 게이트 주소가 아니면 null.
 *
 * `/f/<코드>` 뒤에 붙는 부분은 **앱에 실제로 있는 주소**가 그대로 온다
 * (`/event/bumjin1`, `/mission` ...). 그래서 코드만 떼어내고 나머지를 그대로 넘긴다.
 * 물음표 뒤(쿼리)와 `#` 뒤도 잃지 않고 붙여 보낸다.
 */
export function consumeGate(loc: {
  pathname: string;
  search?: string;
  hash?: string;
}): string | null {
  const matched = /^\/f\/([^/]+)(\/.*)?$/.exec(loc.pathname);
  if (!matched) return null;

  let code = matched[1];
  try {
    code = decodeURIComponent(code);
  } catch {
    // 주소가 깨져 있으면 원문 그대로 본다. 어차피 아래에서 모양을 검사한다.
  }

  saveInflow(code);

  // 코드만 있고 뒤가 없으면 첫 화면으로.
  const rest = matched[2] && matched[2] !== '/' ? matched[2] : '/';
  return `${rest}${loc.search ?? ''}${loc.hash ?? ''}`;
}

/**
 * 게이트 주소로 들어왔으면 **앱이 그려지기 전에** 주소를 바꿔 놓는다.
 *
 * ★ **preact-router 의 `route()` 를 쓰지 않는다.** 그 함수는 이미 화면에 올라간
 *   라우터가 있을 때만 동작한다 — 내부에서 `canRoute()` 로 마운트된 라우터를 먼저
 *   확인하고, 하나도 없으면 주소를 바꾸지 않고 조용히 끝난다.
 *
 *   처음에는 앱의 `useEffect` 안에서 `route()` 를 불렀다. 그 시점은 세션 복원 전이라
 *   아무것도 그리지 않는 동안인데, 바로 그래서 라우터가 없어 **호출이 통째로 무시됐고**
 *   `/f/...` 가 그대로 남아 '페이지를 찾을 수 없습니다' 가 떴다. (2026-09-17)
 *
 * ★ `replaceState` 라 히스토리에 게이트 주소가 남지 않는다. 남기면 뒤로가기가 게이트로
 *   돌아와 그대로 되돌이가 된다.
 */
export function applyGateRedirect(): void {
  try {
    const target = consumeGate(window.location);
    if (target) window.history.replaceState(null, '', target);
  } catch {
    // 주소를 못 바꿔도 앱은 떠야 한다. 유입 경로만 놓칠 뿐이다.
  }
}
